const API_URL = import.meta.env.VITE_API_URL ?? '/api';
const TOKEN_KEY = 'resordo_token';

export type UserRole = 'owner' | 'admin' | 'member' | 'viewer';

export type User = {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  householdId: number;
  emailVerified: boolean;
};

export type AuthResponse = {
  token: string;
  user: User;
};

export type HouseholdMember = {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
};

export type Invite = {
  id: number;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  expiresAt: string;
  createdAt: string;
};

type ApiOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const token = getToken();

  const response = await fetch(`${API_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const errorCode = (data && typeof data === 'object' && 'error' in data
      ? (data as { error: string }).error
      : null) ?? response.statusText;
    throw new ApiError(errorCode, response.status, data);
  }

  return data as T;
}

export type HealthResponse = {
  status: string;
  service: string;
  timestamp: string;
};

export const health = {
  get: () => apiFetch<HealthResponse>('/health'),
};

export const auth = {
  signup: (data: { email: string; password: string; name: string }) =>
    apiFetch<AuthResponse>('/auth/signup', { method: 'POST', body: data }),
  login: (data: { email: string; password: string }) =>
    apiFetch<AuthResponse>('/auth/login', { method: 'POST', body: data }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  me: () => apiFetch<User>('/auth/me'),
};

export const verification = {
  verify: (token: string) =>
    apiFetch<{ success: boolean }>('/verification/verify', {
      method: 'POST',
      body: { token },
    }),
  resend: () => apiFetch<{ success: boolean }>('/verification/resend', { method: 'POST' }),
};

export const passwordReset = {
  request: (email: string) =>
    apiFetch<{ success: boolean }>('/password-reset/request', {
      method: 'POST',
      body: { email },
    }),
  reset: (token: string, password: string) =>
    apiFetch<{ success: boolean }>('/password-reset/reset', {
      method: 'POST',
      body: { token, password },
    }),
};

export const invites = {
  create: (data: { email: string; role: 'admin' | 'member' | 'viewer' }) =>
    apiFetch<{ success: boolean }>('/invites', { method: 'POST', body: data }),
  list: () => apiFetch<Invite[]>('/invites'),
  accept: (data: { token: string; name: string; password: string }) =>
    apiFetch<AuthResponse>('/invites/accept', { method: 'POST', body: data }),
  remove: (id: number) => apiFetch<void>(`/invites/${id}`, { method: 'DELETE' }),
};

export type Household = {
  id: number;
  name: string;
  planCode: string;
  createdAt: string;
};

export const householdApi = {
  get: () => apiFetch<Household>('/household'),
  update: (data: { name: string }) =>
    apiFetch<Household>('/household', { method: 'PATCH', body: data }),
};

export const householdUsers = {
  list: () => apiFetch<HouseholdMember[]>('/users'),
  changeRole: (id: number, role: 'admin' | 'member' | 'viewer') =>
    apiFetch<{ success: boolean }>(`/users/${id}/role`, {
      method: 'PATCH',
      body: { role },
    }),
  remove: (id: number) => apiFetch<void>(`/users/${id}`, { method: 'DELETE' }),
};

// === Kategorier ===

export type Category = {
  id: number;
  householdId: number;
  parentId: number | null;
  name: string;
  description: string | null;
  defaultFields: string[] | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type CategoryInput = {
  name: string;
  description?: string | null;
  parentId?: number | null;
  defaultFields?: string[];
  icon?: string | null;
  color?: string | null;
};

export const categoriesApi = {
  list: () => apiFetch<Category[]>('/categories'),
  get: (id: number) => apiFetch<Category>(`/categories/${id}`),
  create: (data: CategoryInput) =>
    apiFetch<Category>('/categories', { method: 'POST', body: data }),
  update: (id: number, data: Partial<CategoryInput>) =>
    apiFetch<Category>(`/categories/${id}`, { method: 'PATCH', body: data }),
  remove: (id: number) => apiFetch<void>(`/categories/${id}`, { method: 'DELETE' }),
};

// === Lokationer ===

export type Location = {
  id: number;
  householdId: number;
  parentId: number | null;
  name: string;
  description: string | null;
  path: string;
  qrToken: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type LocationInput = {
  name: string;
  description?: string | null;
  parentId?: number | null;
};

export const locationsApi = {
  list: () => apiFetch<Location[]>('/locations'),
  get: (id: number) => apiFetch<Location>(`/locations/${id}`),
  create: (data: LocationInput) =>
    apiFetch<Location>('/locations', { method: 'POST', body: data }),
  update: (id: number, data: Partial<LocationInput>) =>
    apiFetch<Location>(`/locations/${id}`, { method: 'PATCH', body: data }),
  remove: (id: number) => apiFetch<void>(`/locations/${id}`, { method: 'DELETE' }),
};

// === Items ===

export type Item = {
  id: number;
  householdId: number;
  createdByUserId: number;
  categoryId: number | null;
  locationId: number | null;
  name: string;
  description: string | null;
  quantity: string;
  unit: string;
  lowStockThreshold: string | null;
  qrToken: string | null;
  enabledFields: string[] | null;
  // Identifikation
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  // Køb
  purchaseLocation: string | null;
  acquiredAt: string | null;
  priceCents: number | null;
  currency: string;
  warrantyExpiresAt: string | null;
  insuranceValueCents: number | null;
  // Udseende
  color: string | null;
  size: string | null;
  material: string | null;
  // Tilstand
  condition: string | null;
  conditionNote: string | null;
  // Bøger og medier
  author: string | null;
  publisher: string | null;
  publishedYear: number | null;
  isbn: string | null;
  // Køretøj
  registrationNumber: string | null;
  vin: string | null;
  modelYear: number | null;
  createdAt: string;
  updatedAt: string;
};

export type ItemInput = Partial<{
  name: string;
  description: string | null;
  categoryId: number | null;
  locationId: number | null;
  quantity: number;
  unit: string;
  lowStockThreshold: number | null;
  enabledFields: string[];
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  purchaseLocation: string | null;
  acquiredAt: string | null;
  priceCents: number | null;
  currency: string;
  warrantyExpiresAt: string | null;
  insuranceValueCents: number | null;
  color: string | null;
  size: string | null;
  material: string | null;
  condition: string | null;
  conditionNote: string | null;
  author: string | null;
  publisher: string | null;
  publishedYear: number | null;
  isbn: string | null;
  registrationNumber: string | null;
  vin: string | null;
  modelYear: number | null;
}> & { name: string };

export type ItemFilters = {
  categoryId?: number;
  locationId?: number;
  search?: string;
};

export const itemsApi = {
  list: (filters: ItemFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.categoryId) params.set('categoryId', String(filters.categoryId));
    if (filters.locationId) params.set('locationId', String(filters.locationId));
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString();
    return apiFetch<Item[]>(`/items${qs ? `?${qs}` : ''}`);
  },
  get: (id: number) => apiFetch<Item>(`/items/${id}`),
  create: (data: ItemInput) => apiFetch<Item>('/items', { method: 'POST', body: data }),
  update: (id: number, data: Partial<ItemInput>) =>
    apiFetch<Item>(`/items/${id}`, { method: 'PATCH', body: data }),
  remove: (id: number) => apiFetch<void>(`/items/${id}`, { method: 'DELETE' }),
};

// === Media (billeder + kvitteringer) ===

export type MediaKind = 'photo' | 'receipt';

export type ItemMedia = {
  id: number;
  itemId: number | null;
  categoryId: number | null;
  locationId: number | null;
  kind: MediaKind;
  storageBackend: 'local' | 'bunny';
  storageKey: string;
  thumbnailKey: string | null;
  mimeType: string;
  sizeBytes: number;
  originalFilename: string | null;
  sortOrder: number;
  createdAt: string;
};

export type MediaTarget = 'items' | 'categories' | 'locations';

const API_BASE = API_URL;

const BUNNY_CDN_URL =
  (import.meta.env.VITE_BUNNY_CDN_URL as string | undefined) ?? 'https://resordo-media.b-cdn.net';

/**
 * Bygger URL til at vise et media. Bunny-medier serveres direkte fra CDN
 * (UUID i sti er sikkerhedsmekanismen). Local-medier serveres via vores
 * backend med JWT i query-param.
 */
export function mediaUrl(media: ItemMedia, options: { thumbnail?: boolean } = {}): string {
  const useThumb = !!options.thumbnail && !!media.thumbnailKey;
  if (media.storageBackend === 'bunny') {
    const key = useThumb ? media.thumbnailKey! : media.storageKey;
    return `${BUNNY_CDN_URL.replace(/\/$/, '')}/${key}`;
  }
  const params = new URLSearchParams();
  const token = getToken();
  if (token) params.set('token', token);
  if (useThumb) params.set('thumbnail', '1');
  return `${API_BASE}/media/${media.id}?${params.toString()}`;
}

export const mediaApi = {
  listForItem: (itemId: number) => apiFetch<ItemMedia[]>(`/items/${itemId}/media`),
  listForCategory: (categoryId: number) =>
    apiFetch<ItemMedia[]>(`/categories/${categoryId}/media`),
  listForLocation: (locationId: number) =>
    apiFetch<ItemMedia[]>(`/locations/${locationId}/media`),
  // Backwards compat alias
  list: (itemId: number) => apiFetch<ItemMedia[]>(`/items/${itemId}/media`),
  upload: async (
    target: MediaTarget,
    targetId: number,
    file: Blob,
    kind: MediaKind,
    originalFilename?: string,
  ) => {
    const form = new FormData();
    form.append('file', file, originalFilename ?? 'upload');
    form.append('kind', kind);
    const token = getToken();
    const response = await fetch(`${API_BASE}/${target}/${targetId}/media`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: response.statusText }));
      throw new ApiError(data.error ?? 'UPLOAD_FAILED', response.status, data);
    }
    return (await response.json()) as ItemMedia;
  },
  remove: (id: number) => apiFetch<void>(`/media/${id}`, { method: 'DELETE' }),
};

// === Links ===

export type ItemLink = {
  id: number;
  itemId: number;
  url: string;
  label: string | null;
  sortOrder: number;
  createdAt: string;
};

export const linksApi = {
  list: (itemId: number) => apiFetch<ItemLink[]>(`/items/${itemId}/links`),
  create: (itemId: number, data: { url: string; label?: string | null }) =>
    apiFetch<ItemLink>(`/items/${itemId}/links`, { method: 'POST', body: data }),
  update: (id: number, data: { url?: string; label?: string | null }) =>
    apiFetch<ItemLink>(`/links/${id}`, { method: 'PATCH', body: data }),
  remove: (id: number) => apiFetch<void>(`/links/${id}`, { method: 'DELETE' }),
};

// === Storage usage ===

export type StorageUsage = {
  bytesUsed: number;
  bytesAllowed: number;
  mediaCount: number;
  percentUsed: number;
};

export const storageApi = {
  usage: () => apiFetch<StorageUsage>('/storage/usage'),
};
